/* eslint-disable no-magic-numbers */
import { round } from 'lodash';

import { TPlaceOrderSide } from '@trader/types';
import { defaultPipSize } from '@trader/constants';

import { formatByPipSize, getPipSizeNumber } from '../trading';

export const maxTakeProfitRateFallback = 999999999.99;

export const getOrderBasePrice = (
  side: TPlaceOrderSide,
  bidPrice: number,
  askPrice: number
) => (side === 'Buy' ? bidPrice : askPrice);

const getTradingAmplitude = (stopsLevel: number, pipSize: number) =>
  stopsLevel * +pipSize;

const getProfit = (
  quantity: number,
  rate: string,
  side: TPlaceOrderSide,
  bidPrice: number,
  askPrice: number
) => {
  return (
    quantity * Math.abs(+rate - getOrderBasePrice(side, bidPrice, askPrice))
  );
};

export const getModelPips = (
  stopsLevel: number,
  category: string | undefined
): string => {
  return String(stopsLevel / (category === 'Indices' ? 10 : 1));
};

export const getRealPipsFromModel = (
  pips: string,
  category: string | undefined
): number => {
  return +pips * (category === 'Indices' ? 10 : 1);
};

const getAmplitudeFromModel = (
  pips: number | undefined,
  pipSize: number,
  category: string | undefined
) => {
  return getTradingAmplitude(
    getRealPipsFromModel(String(pips ?? 0), category),
    pipSize ?? defaultPipSize
  );
};

export const handleIndicesPipSize = (
  pipSize: number,
  category: string | undefined,
  minOrderSizeIncrement: number
): number => {
  if (category === 'Indices') {
    const increment = minOrderSizeIncrement ?? 1;
    return round(
      increment > 1 ? +pipSize * increment : +pipSize / increment,
      getPipSizeNumber(pipSize)
    );
  }
  return pipSize;
};

export const getTradingStops = (
  orderSide: TPlaceOrderSide,
  bidPrice: number,
  askPrice: number,
  stopsLevel: number,
  pipSize: number
) => {
  const basePrice = getOrderBasePrice(orderSide, bidPrice, askPrice);
  const instrumentPriceTradingDiff = getTradingAmplitude(stopsLevel, pipSize);

  const stopsEnd = round(
    Number(basePrice) + Number(instrumentPriceTradingDiff),
    getPipSizeNumber(pipSize)
  );

  const stopsStart = round(
    Number(basePrice) - Number(instrumentPriceTradingDiff),
    getPipSizeNumber(pipSize)
  );

  return { stopsStart, stopsEnd };
};

export const getConvertedProfit = (
  quantity: number,
  takeProfitRate: string,
  side: TPlaceOrderSide,
  bidPrice: number,
  askPrice: number,
  currencyConvert: number
) =>
  getProfit(quantity, takeProfitRate, side, bidPrice, askPrice) /
  currencyConvert;

export const getConvertedLoss = (
  quantity: number,
  stopLossRate: string,
  side: TPlaceOrderSide,
  bidPrice: number,
  askPrice: number,
  currencyConvert: number
) =>
  getLoss(quantity, stopLossRate, side, bidPrice, askPrice) / currencyConvert;

export const getProfitPipsFromRate = (
  rate: string,
  side: TPlaceOrderSide,
  bidPrice: number,
  askPrice: number,
  pipSize: number,
  category: string | undefined,
  formattingPipSize?: number
) => {
  const profit = +rate - getOrderBasePrice(side, bidPrice, askPrice);

  return +formatByPipSize(
    getModelPips((profit * (side === 'Buy' ? 1 : -1)) / +pipSize, category),
    formattingPipSize ?? pipSize
  );
};

export const getProfitRateFromPips = (
  pips: string,
  side: TPlaceOrderSide,
  bidPrice: number,
  askPrice: number,
  pipSize: number,
  category: string | undefined,
  formattingPipSize?: number
) => {
  return formatByPipSize(
    getOrderBasePrice(side, bidPrice, askPrice) +
      +pipSize *
        getRealPipsFromModel(pips, category) *
        (side === 'Buy' ? 1 : -1),
    formattingPipSize ?? pipSize
  );
};

export const getBuyProfitRateWithAmplitude = (
  pips: number | undefined,
  pipSize: number,
  bidPrice: number | string | undefined,
  formattingPipSize: number | undefined,
  category: string | undefined
) => {
  const amplitude = getAmplitudeFromModel(pips, pipSize, category);

  return formatByPipSize(
    Number(bidPrice ?? 0) + amplitude,
    formattingPipSize ?? defaultPipSize
  );
};

export const getSellProfitRateWithAmplitude = (
  stopsLevel: number | undefined,
  pipSize: number,
  askPrice: number | string | undefined,
  formattingPipSize: number | undefined,
  category: string | undefined
) => {
  const amplitude = getAmplitudeFromModel(stopsLevel, pipSize, category);

  return formatByPipSize(
    Number(askPrice ?? 0) - amplitude,
    formattingPipSize ?? defaultPipSize
  );
};

export const getMinTakeProfitRate = (
  stopsLevel: number | undefined,
  pipSize: number,
  side: TPlaceOrderSide,
  bidPrice: number | string | undefined,
  formattingPipSize: number
) => {
  return side === 'Buy'
    ? getBuyProfitRateWithAmplitude(
        stopsLevel,
        pipSize,
        bidPrice,
        formattingPipSize,
        undefined
      )
    : +(pipSize ?? defaultPipSize);
};

export const getMaxTakeProfitRate = (
  stopsLevel: number | undefined,
  pipSize: number,
  side: TPlaceOrderSide,
  askPrice: number | string | undefined,
  formattingPipSize: number
) => {
  return side === 'Buy'
    ? maxTakeProfitRateFallback
    : getSellProfitRateWithAmplitude(
        stopsLevel,
        pipSize,
        askPrice,
        formattingPipSize,
        undefined
      );
};

const getLossBasePrice = (
  side: TPlaceOrderSide,
  bidPrice: number,
  askPrice: number
) => (side === 'Buy' ? bidPrice : askPrice);

const getLoss = (
  quantity: number,
  rate: string,
  side: TPlaceOrderSide,
  bidPrice: number,
  askPrice: number
) => quantity * (getLossBasePrice(side, bidPrice, askPrice) - +rate);

export const getBuyLossRateWithAmplitude = (
  stopsLevel: number | undefined,
  pipSize: number,
  bidPrice: number | string | undefined,
  formattingPipSize: number | undefined,
  category: string | undefined
) => {
  const amplitude = getAmplitudeFromModel(stopsLevel, pipSize, category);

  return formatByPipSize(
    Number(bidPrice ?? 0) - amplitude,
    formattingPipSize ?? defaultPipSize
  );
};

export const getSellLossRateWithAmplitude = (
  stopsLevel: number | undefined,
  pipSize: number,
  askPrice: number | string | undefined,
  formattingPipSize: number | undefined,
  category: string | undefined
) => {
  const amplitude = getAmplitudeFromModel(
    stopsLevel,
    pipSize ?? defaultPipSize,
    category
  );

  return formatByPipSize(
    Number(askPrice ?? 0) + amplitude,
    formattingPipSize ?? defaultPipSize
  );
};

export const getLossPipsFromRate = (
  rate: string,
  side: TPlaceOrderSide,
  bidPrice: number,
  askPrice: number,
  pipSize: number,
  category: string | undefined,
  formattingPipSize?: number
) =>
  +formatByPipSize(
    getModelPips(
      (getLoss(1, rate, side, bidPrice, askPrice) * (side === 'Buy' ? 1 : -1)) /
        +pipSize,
      category
    ),
    formattingPipSize ?? pipSize
  );
export const getLossRateFromPips = (
  pips: string,
  side: TPlaceOrderSide,
  bidPrice: number,
  askPrice: number,
  pipSize: number,
  category: string | undefined,
  formattingPipSize?: number
) => {
  const basePrice = getLossBasePrice(side, bidPrice, askPrice);
  return formatByPipSize(
    basePrice +
      +pipSize *
        getRealPipsFromModel(pips, category) *
        (side === 'Buy' ? -1 : 1),
    formattingPipSize ?? pipSize
  );
};

export const getMinStopLoss = (
  stopsLevel: number | undefined,
  pipSize: number,
  side: TPlaceOrderSide,
  askPrice: number | string | undefined,
  formattingPipSize: number
) => {
  return side === 'Buy'
    ? +(pipSize ?? defaultPipSize)
    : getSellLossRateWithAmplitude(
        stopsLevel,
        pipSize ?? defaultPipSize,
        askPrice,
        formattingPipSize ?? defaultPipSize,
        undefined
      );
};

export const getMaxStopLoss = (
  stopsLevel: number | undefined,
  pipSize: number,
  side: TPlaceOrderSide,
  bidPrice: number | string | undefined,
  formattingPipSize?: number
) => {
  return side === 'Buy'
    ? getBuyLossRateWithAmplitude(
        stopsLevel,
        pipSize,
        bidPrice,
        formattingPipSize ?? pipSize,
        undefined
      )
    : maxTakeProfitRateFallback;
};
